import { z } from 'zod';

export enum AnalysisRowType {
  AGGREGATE = 'AGGREGATE',
  TIMEPOINT = 'TIMEPOINT',
}

const YyyyMmDd = /^\d{4}-\d{2}-\d{2}$/;

const errorMeasuresShape = {
  wape: z.number().nullable(),
  mae: z.number().nullable(),
  rmse: z.number().nullable(),
  rrse: z.number().nullable(),
  rae: z.number().nullable(),
  smape: z.number().nullable(),
  mase: z.number().nullable(),
  mape: z.number().nullable(),
  me: z.number().nullable(),
  wpe: z.number().nullable(),
  mpe: z.number().nullable(),
  capped_wape: z.number().nullable(),
  capped_mape: z.number().nullable(),
} as const;

const AggregateComparisonTimeseriesSchema = z.object({
  timeseriesName: z.string(),
  predictionSum: z.number().nullable(),
  ...errorMeasuresShape,
});

const TimepointComparisonTimeseriesSchema = z.object({
  timeseriesName: z.string(),
  prediction: z.number().nullable(),
});

const aggregateShape = {
  id: z.string().uuid(),
  groupCombination: z.record(
    z.string(),
    z.union([z.string(), z.number(), z.boolean(), z.null()]),
  ),
  comparisonTimeseriesNames: z.array(AggregateComparisonTimeseriesSchema),
  ...errorMeasuresShape,
  analysisVersionId: z.string().uuid(),
  predictionSum: z.number().nullable(),
  truthSum: z.number().nullable(),
} as const;

const timepointShape = {
  analysisVersionGroupId: z.string().uuid(),
  comparisonTimeseriesNames: z.array(TimepointComparisonTimeseriesSchema),
  tsDate: z.string().regex(YyyyMmDd, 'tsDate must be in YYYY-MM-DD format'),
  forecastId: z.string().nullable(),
  truth: z.number().nullable(),
  prediction: z.number().nullable(),
  relativeError: z.number().nullable(),
  absoluteError: z.number().nullable(),
  versionId: z.string().nullable(),
} as const;

const baseTreeShape = {
  path: z.array(z.string()),
} as const;

const AggregateRowSchema = z
  .object({
    rowType: z.literal(AnalysisRowType.AGGREGATE),
  })
  .extend(aggregateShape)
  .extend(baseTreeShape);

const TimepointRowSchema = z
  .object({
    rowType: z.literal(AnalysisRowType.TIMEPOINT),
  })
  .extend(timepointShape)
  .extend(baseTreeShape);

export const AnalysisVersionGroupResponseSchema = z.discriminatedUnion(
  'rowType',
  [AggregateRowSchema, TimepointRowSchema],
);

export type AnalysisVersionGroupResponse = z.infer<
  typeof AnalysisVersionGroupResponseSchema
>;
export type AnalysisAggregateRow = z.infer<typeof AggregateRowSchema>;
export type AnalysisTimepointRow = z.infer<typeof TimepointRowSchema>;
export type AnalysisAggregateComparisonTimeseries = z.infer<
  typeof AggregateComparisonTimeseriesSchema
>;
export type AnalysisTimepointComparisonTimeseries = z.infer<
  typeof TimepointComparisonTimeseriesSchema
>;
